import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Download, Users, MessageSquare, TrendingUp, Search, User, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import { useCustomRSVP } from '@/hooks/useCustomRSVP';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';

interface RSVPAnalyticsDashboardProps {
  eventId: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export function RSVPAnalyticsDashboard({ eventId }: RSVPAnalyticsDashboardProps) {
  const isMobile = useIsMobile();
  const { responses, rsvpForm, exportResponses, getResponseAnalytics } = useCustomRSVP(eventId);
  
  const [guestData, setGuestData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGuest, setExpandedGuest] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'overview' | 'guests'>('overview');
  
  const analytics = getResponseAnalytics();

  // Load guest information
  useEffect(() => {
    const loadGuestData = async () => {
      if (!responses.length) return;

      const { data: guests, error } = await supabase
        .from('event_guests')
        .select('id, name, email')
        .eq('event_id', eventId);

      if (error) {
        console.error('Error loading guest data:', error);
        return;
      }

      const guestMap = new Map(guests?.map(g => [g.id, g]) || []);
      
      // Group responses by guest
      const responsesByGuest = responses.reduce((acc, response) => {
        const guestId = response.guest_id;
        if (!acc[guestId]) {
          acc[guestId] = {
            guest: guestMap.get(guestId) || { id: guestId, name: 'Unknown', email: 'Unknown' },
            responses: []
          };
        }
        acc[guestId].responses.push(response);
        return acc;
      }, {} as Record<string, { guest: any; responses: any[] }>);

      setGuestData(Object.values(responsesByGuest));
    };

    loadGuestData();
  }, [responses, eventId]);

  if (!analytics || !rsvpForm) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">No RSVP responses yet</p>
        </CardContent>
      </Card>
    );
  }

  const completionRate = rsvpForm.questions.length > 0 
    ? (analytics.totalResponses / rsvpForm.questions.length) * 100 
    : 0;

  // Filter guests by search term
  const filteredGuests = guestData.filter(({ guest }) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      guest.name?.toLowerCase().includes(search) ||
      guest.email?.toLowerCase().includes(search)
    );
  });

  // Get answer for a specific question from guest responses
  const getGuestAnswer = (guestResponses: any[], questionId: string) => {
    const response = guestResponses.find(r => r.question_id === questionId);
    if (!response) return '-';
    
    try {
      const answer = typeof response.answer === 'string' 
        ? JSON.parse(response.answer) 
        : response.answer;
      return Array.isArray(answer) ? answer.join(', ') : answer;
    } catch {
      return response.answer;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base md:text-lg font-semibold">RSVP Analytics</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Response data and insights for your custom RSVP form
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={() => exportResponses('csv')}
            size={isMobile ? "sm" : "default"}
            className="flex-1 sm:flex-none"
          >
            <Download className="h-3 w-3 md:h-4 md:w-4 mr-2" />
            {isMobile ? 'CSV' : 'Export CSV'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => exportResponses('json')}
            size={isMobile ? "sm" : "default"}
            className="flex-1 sm:flex-none"
          >
            <Download className="h-3 w-3 md:h-4 md:w-4 mr-2" />
            {isMobile ? 'JSON' : 'Export JSON'}
          </Button>
        </div>
      </div>
      
      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={(v) => setActiveView(v as 'overview' | 'guests')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="guests" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            By Guest
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 md:space-y-6 mt-4">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Total Responses</CardTitle>
            <Users className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">{analytics.totalResponses}</div>
            <p className="text-xs text-muted-foreground">
              {isMobile ? 'Guests responded' : 'Unique guests who responded'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Questions</CardTitle>
            <MessageSquare className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">{analytics.totalQuestions}</div>
            <p className="text-xs text-muted-foreground">
              {isMobile ? 'In form' : 'Custom questions in form'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Completion Rate</CardTitle>
            <TrendingUp className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">{completionRate.toFixed(1)}%</div>
            <Progress value={completionRate} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Question Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {analytics.questionAnalytics.map((questionData, index) => (
          <Card key={questionData.question.id}>
            <CardHeader>
              <CardTitle className="text-sm md:text-base">{questionData.question.text}</CardTitle>
              <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                <Badge variant="secondary" className="text-xs">
                  {questionData.responseCount} {isMobile ? '' : 'responses'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {questionData.question.type}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {questionData.type === 'categorical' && questionData.optionCounts ? (
                <div className="space-y-3 md:space-y-4">
                  {/* Bar Chart for categorical data */}
                  <ResponsiveContainer width="100%" height={isMobile ? 180 : 200}>
                    <BarChart data={Object.entries(questionData.optionCounts).map(([option, count]) => ({
                      option,
                      count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="option" 
                        fontSize={isMobile ? 10 : 12}
                        angle={-45}
                        textAnchor="end"
                        height={isMobile ? 50 : 60}
                      />
                      <YAxis fontSize={isMobile ? 10 : 12} />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS[index % COLORS.length]} />
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Response breakdown */}
                  <div className="space-y-2">
                    {Object.entries(questionData.optionCounts).map(([option, count]) => (
                      <div key={option} className="flex items-center justify-between text-sm">
                        <span>{option}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{count}</span>
                          <span className="text-muted-foreground">
                            ({questionData.responseCount > 0 
                              ? ((count / questionData.responseCount) * 100).toFixed(1)
                              : 0}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {questionData.responseCount} open-ended responses
                  </p>
                  {questionData.responses && questionData.responses.length > 0 && (
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {(questionData.responses as string[]).slice(0, 5).map((response, idx) => (
                        <div key={idx} className="text-sm bg-muted p-2 rounded">
                          {Array.isArray(response) ? response.join(', ') : response}
                        </div>
                      ))}
                      {questionData.responses.length > 5 && (
                        <p className="text-xs text-muted-foreground">
                          +{questionData.responses.length - 5} more responses
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
        </TabsContent>

        <TabsContent value="guests" className="space-y-4 mt-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search guests by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {filteredGuests.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">
                  {searchTerm ? 'No guests found matching your search' : 'No responses yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {isMobile ? (
                // Mobile: Expandable cards
                filteredGuests.map(({ guest, responses: guestResponses }) => (
                  <Card key={guest.id}>
                    <CardHeader 
                      className="cursor-pointer"
                      onClick={() => setExpandedGuest(expandedGuest === guest.id ? null : guest.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-sm">{guest.name}</p>
                            <p className="text-xs text-muted-foreground">{guest.email}</p>
                          </div>
                        </div>
                        {expandedGuest === guest.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </CardHeader>
                    {expandedGuest === guest.id && (
                      <CardContent className="space-y-3 pt-0">
                        {rsvpForm?.questions.map((question) => (
                          <div key={question.id} className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">
                              {question.text}
                            </p>
                            <p className="text-sm">
                              {getGuestAnswer(guestResponses, question.id)}
                            </p>
                          </div>
                        ))}
                      </CardContent>
                    )}
                  </Card>
                ))
              ) : (
                // Desktop: Table view
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Guest</TableHead>
                        {rsvpForm?.questions.map((question) => (
                          <TableHead key={question.id}>{question.text}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredGuests.map(({ guest, responses: guestResponses }) => (
                        <TableRow key={guest.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{guest.name}</p>
                              <p className="text-xs text-muted-foreground">{guest.email}</p>
                            </div>
                          </TableCell>
                          {rsvpForm?.questions.map((question) => (
                            <TableCell key={question.id}>
                              {getGuestAnswer(guestResponses, question.id)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}